import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useContractTypes } from "@/hooks/useContractTypes";
import { PaymentFrequencyUnit, ContractDirection } from "@/lib/types";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { IncomingIcon, OutgoingIcon, EmploymentIcon, CircleDotIcon } from "./ContractDirectionIcons";

// Define the form schema with conditional validation for renewal fields
const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  description: z.string().optional(),
  auto_renews: z.boolean().default(false),
  renewal_interval: z.coerce.number().optional(),
  renewal_unit: z.enum(["Day", "Month", "Year"]).optional(),
  direction: z.enum(["incoming", "outgoing", "employment", "other"]).default("other"),
}).refine((data) => {
  // If auto_renews is true, require renewal_interval and renewal_unit
  if (data.auto_renews) {
    return data.renewal_interval !== undefined && data.renewal_unit !== undefined;
  }
  // Otherwise, no need to validate these fields
  return true;
}, {
  message: "Renewal interval and unit are required when auto renewal is enabled",
  path: ["renewal_interval"] // Show the error on the renewal_interval field
});

// Define the type for form values to match the schema
type FormValues = z.infer<typeof formSchema>;

interface ContractTypeFormProps {
  mode: "create" | "edit";
  contractType?: {
    id: string;
    name: string;
    description?: string;
    auto_renews?: boolean;
    renewal_interval?: number;
    renewal_unit?: PaymentFrequencyUnit;
    direction?: ContractDirection;
  };
}

export function ContractTypeForm({ mode, contractType }: ContractTypeFormProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { 
    useCreateContractTypeMutation, 
    useUpdateContractTypeMutation,
    useDeleteContractTypeMutation
  } = useContractTypes();
  
  const createContractType = useCreateContractTypeMutation();
  const updateContractType = useUpdateContractTypeMutation();
  const deleteContractType = useDeleteContractTypeMutation();

  console.log("Contract type in form:", contractType);

  // Initialize form with default values or existing contract type data
  const defaultValues: FormValues = {
    name: contractType?.name || "",
    description: contractType?.description || "",
    auto_renews: contractType?.auto_renews || false,
    renewal_interval: contractType?.renewal_interval || undefined,
    renewal_unit: contractType?.renewal_unit || undefined,
    direction: (contractType?.direction || "other") as "incoming" | "outgoing" | "employment" | "other",
  };

  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Watch auto_renews value to conditionally display renewal fields
  const autoRenews = form.watch("auto_renews");
  const selectedDirection = form.watch("direction");

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      console.log("Form submit data:", data);
      
      if (mode === "create") {
        await createContractType.mutateAsync({
          name: data.name,
          description: data.description,
          auto_renews: data.auto_renews,
          renewal_interval: data.auto_renews ? data.renewal_interval : undefined,
          renewal_unit: data.auto_renews ? data.renewal_unit : undefined,
          direction: data.direction as ContractDirection
        });
        navigate("/contract-types");
      } else if (mode === "edit" && contractType) {
        console.log("Updating contract type:", {
          id: contractType.id,
          name: data.name,
          description: data.description,
          auto_renews: data.auto_renews,
          renewal_interval: data.auto_renews ? data.renewal_interval : undefined,
          renewal_unit: data.auto_renews ? data.renewal_unit : undefined,
          direction: data.direction as ContractDirection
        });
        
        // Update with all the necessary properties directly
        await updateContractType.mutateAsync({
          id: contractType.id,
          name: data.name,
          description: data.description,
          auto_renews: data.auto_renews,
          renewal_interval: data.auto_renews ? data.renewal_interval : undefined,
          renewal_unit: data.auto_renews ? data.renewal_unit : undefined,
          direction: data.direction as ContractDirection
        });
        navigate("/contract-types");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!contractType) return;
    
    try {
      setIsSubmitting(true);
      await deleteContractType.mutateAsync(contractType.id);
      navigate("/contract-types");
    } catch (error) {
      console.error("Error deleting contract type:", error);
      setIsSubmitting(false);
    }
  };

  // Make renewal fields optional when auto_renews is false
  // and required when auto_renews is true
  React.useEffect(() => {
    if (autoRenews) {
      form.register("renewal_interval", { required: "Renewal interval is required" });
      form.register("renewal_unit", { required: "Renewal unit is required" });
    } else {
      form.unregister("renewal_interval");
      form.unregister("renewal_unit");
    }
  }, [autoRenews, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Contract type name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Contract type description (optional)"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="direction"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Contract Direction/Purpose</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-col space-y-1"
                    >
                      <Card className="border rounded-md p-0 overflow-hidden">
                        <CardContent className="p-0">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                            <FormItem className={`flex items-center space-x-3 space-y-0 p-4 cursor-pointer hover:bg-muted/50 ${field.value === "incoming" ? "bg-muted" : ""}`}>
                              <label className="flex flex-1 items-center cursor-pointer">
                                <RadioGroupItem value="incoming" className="peer sr-only" />
                                <div className="flex items-center gap-3 peer-data-[state=checked]:font-medium">
                                  <IncomingIcon className="h-5 w-5 text-emerald-500" />
                                  <div className="flex-1">
                                    <div className="font-medium">Incoming</div>
                                    <div className="text-sm text-muted-foreground">Contracts where you are receiving</div>
                                  </div>
                                  {field.value === "incoming" && <Check className="h-4 w-4 text-primary" />}
                                </div>
                              </label>
                            </FormItem>
                            
                            <FormItem className={`flex items-center space-x-3 space-y-0 p-4 cursor-pointer hover:bg-muted/50 ${field.value === "outgoing" ? "bg-muted" : ""}`}>
                              <label className="flex flex-1 items-center cursor-pointer">
                                <RadioGroupItem value="outgoing" className="peer sr-only" />
                                <div className="flex items-center gap-3 peer-data-[state=checked]:font-medium">
                                  <OutgoingIcon className="h-5 w-5 text-orange-500" />
                                  <div className="flex-1">
                                    <div className="font-medium">Outgoing</div>
                                    <div className="text-sm text-muted-foreground">Contracts where you are paying</div>
                                  </div>
                                  {field.value === "outgoing" && <Check className="h-4 w-4 text-primary" />}
                                </div>
                              </label>
                            </FormItem>
                            
                            <FormItem className={`flex items-center space-x-3 space-y-0 p-4 cursor-pointer hover:bg-muted/50 ${field.value === "employment" ? "bg-muted" : ""}`}>
                              <label className="flex flex-1 items-center cursor-pointer">
                                <RadioGroupItem value="employment" className="peer sr-only" />
                                <div className="flex items-center gap-3 peer-data-[state=checked]:font-medium">
                                  <EmploymentIcon className="h-5 w-5 text-purple-500" />
                                  <div className="flex-1">
                                    <div className="font-medium">Employment</div>
                                    <div className="text-sm text-muted-foreground">Employment/salary related</div>
                                  </div>
                                  {field.value === "employment" && <Check className="h-4 w-4 text-primary" />}
                                </div>
                              </label>
                            </FormItem>
                            
                            <FormItem className={`flex items-center space-x-3 space-y-0 p-4 cursor-pointer hover:bg-muted/50 ${field.value === "other" ? "bg-muted" : ""}`}>
                              <label className="flex flex-1 items-center cursor-pointer">
                                <RadioGroupItem value="other" className="peer sr-only" />
                                <div className="flex items-center gap-3 peer-data-[state=checked]:font-medium">
                                  <CircleDotIcon className="h-5 w-5 text-blue-500" />
                                  <div className="flex-1">
                                    <div className="font-medium">Other</div>
                                    <div className="text-sm text-muted-foreground">Other contract type</div>
                                  </div>
                                  {field.value === "other" && <Check className="h-4 w-4 text-primary" />}
                                </div>
                              </label>
                            </FormItem>
                          </div>
                        </CardContent>
                      </Card>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-2">Auto-Renewal Settings</h3>
              <Separator className="mb-4" />
              
              <FormField
                control={form.control}
                name="auto_renews"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 mb-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Auto Renewal</FormLabel>
                      <FormDescription>
                        Does this contract type automatically renew?
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              {autoRenews && (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="renewal_interval"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Renewal Interval</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            placeholder="Renewal interval"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="renewal_unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Renewal Unit</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select frequency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Day">Days</SelectItem>
                            <SelectItem value="Month">Months</SelectItem>
                            <SelectItem value="Year">Years</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/contract-types")}
          >
            Cancel
          </Button>
          {mode === "edit" && (
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              Delete
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : mode === "create" ? "Create" : "Update"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
